import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, memo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { validationSchema } from './constants';
import { PmiTextField } from 'shell/components/shared/text-field';
import { DateTimePicker } from '@mui/lab';
import ErrorsTable from './errors-table';
import { DateTime } from 'luxon';
import { EventProcessingJob } from 'shell/models/job-execution';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { useParams } from 'react-router-dom';
import { __OPERATION__BATCH__ } from 'shell/constants/environment';
import { AxiosResponse } from 'axios';

export interface ProcessingJobForm {
  id?: string;
  description?: string;
  state?: string;
  sent?: DateTime | null;
  from?: DateTime | null;
  to?: DateTime | null;
  numInput?: string;
  numOutput?: string;
  numError?: string;
  numWarning?: string;
  reason?: string;
  events?: Array<EventProcessingJob>;
}

interface FormProcessingJobProps {
  onOk: (form: ProcessingJobForm) => void;
  onCancel: (form?: ProcessingJobForm) => void;
  title: string;
  disabled?: boolean;
  initialValues?: ProcessingJobForm;
}

const FormProcessingJobComponent = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
}: FormProcessingJobProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const filtersProcessingJobForm = useFormik<ProcessingJobForm>({
    initialValues: initialValues || {
      id: '',
      description: '',
      from: null,
      to: null,
      numError: '',
      numInput: '',
      numOutput: '',
      numWarning: '',
      sent: null,
      state: '',
      events: [],
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const paramsObj = useParams();

  const [processingJobEvents] = useHttpFetch<
    HttpResponseList<EventProcessingJob>,
    Array<EventProcessingJob>
  >(`${__OPERATION__BATCH__}/events?jobExecutionId=${paramsObj?.id}&sort=receivedAt`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: (response: AxiosResponse<HttpResponseList<EventProcessingJob>>) => {
      const data: Array<EventProcessingJob> = response?.data?.payload?.list?.map((el) => {
        return {
          id: el?.id,
          event: el?.event,
          message: el?.message,
          receivedAt: el?.receivedAt
            ? DateTime.fromISO(el?.receivedAt)?.toFormat('dd/MM/yyyy HH:mm:ss')
            : '',
        };
      });
      return data;
    },
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={filtersProcessingJobForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Codice"
                  label="Codice"
                  id="id"
                  name="id"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={filtersProcessingJobForm.values.id || ''}
                  disabled={disabled}
                  onChange={filtersProcessingJobForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="Descrizione"
                  label="Descrizione"
                  id="description"
                  name="description"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={filtersProcessingJobForm.values.description || ''}
                  disabled={disabled}
                  onChange={filtersProcessingJobForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Stato"
                  label="Stato"
                  id="state"
                  name="state"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={filtersProcessingJobForm.values.state || ''}
                  disabled={disabled}
                  onChange={filtersProcessingJobForm.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DateTimePicker
                  label="Data e ora inoltro"
                  value={filtersProcessingJobForm.values.sent || null}
                  onChange={(date) => filtersProcessingJobForm.setFieldValue('sent', date)}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      fullWidth
                      error={
                        filtersProcessingJobForm.touched.sent &&
                        Boolean(filtersProcessingJobForm.errors.sent)
                      }
                      helperText={filtersProcessingJobForm.errors.sent}
                    />
                  )}
                />
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="Data e ora inizio elaborazione"
                    value={filtersProcessingJobForm.values.from || null}
                    onChange={(date) => filtersProcessingJobForm.setFieldValue('from', date)}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        fullWidth
                        error={
                          filtersProcessingJobForm.touched.from &&
                          Boolean(filtersProcessingJobForm.errors.from)
                        }
                        helperText={filtersProcessingJobForm.errors.from}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="Data e ora fine elaborazione"
                    value={filtersProcessingJobForm.values.to || null}
                    onChange={(date) => filtersProcessingJobForm.setFieldValue('to', date)}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        fullWidth
                        error={
                          filtersProcessingJobForm.touched.to &&
                          Boolean(filtersProcessingJobForm.errors.to)
                        }
                        helperText={filtersProcessingJobForm.errors.to}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    aria-label="Num Input"
                    label="Num Input"
                    id="numInput"
                    name="numInput"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={filtersProcessingJobForm.values.numInput || ''}
                    disabled={disabled}
                    onChange={filtersProcessingJobForm.handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    aria-label="Num Output"
                    label="Num Output"
                    id="numOutput"
                    name="numOutput"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={filtersProcessingJobForm.values.numOutput || ''}
                    disabled={disabled}
                    onChange={filtersProcessingJobForm.handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    aria-label="Num errori"
                    label="Num errori"
                    id="numErrori"
                    name="numErrori"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={filtersProcessingJobForm.values.numError || ''}
                    disabled={disabled}
                    onChange={filtersProcessingJobForm.handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    aria-label="Num warning"
                    label="Num warning"
                    id="numWarning"
                    name="numWarning"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={filtersProcessingJobForm.values.numWarning || ''}
                    disabled={disabled}
                    onChange={filtersProcessingJobForm.handleChange}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <ErrorsTable
                  body={((processingJobEvents?.data || []) as Array<EventProcessingJob>) || []}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(filtersProcessingJobForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormProcessingJobComponent.displayName = 'Form processing job component';

const FormProcessingJob = memo(FormProcessingJobComponent);

export default FormProcessingJob;
